import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';
import SearchBar from '../components/ui/SearchBar';
import AirDatePicker from '../components/ui/AirDatePicker';

function BankTransactions() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [bank, setBank] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    // Filters
    const [draftStartDate, setDraftStartDate] = useState('');
    const [draftEndDate, setDraftEndDate] = useState('');
    const [draftType, setDraftType] = useState('All Types');

    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');
    const [appliedType, setAppliedType] = useState('All Types');

    const fetchData = async () => {
        try {
            setLoading(true);
            const result = await fetchApi(`/bank/ledger/${id}`);
            
            if (result) {
                setBank(result.bank);
                setLedger(result.ledger || []);
            }
        } catch (error) {
            console.error('Error fetching bank ledger:', error);
            toast.error('Failed to fetch transactions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleSubmitFilters = () => {
        setAppliedStartDate(draftStartDate);
        setAppliedEndDate(draftEndDate);
        setAppliedType(draftType);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setDraftStartDate('');
        setDraftEndDate('');
        setDraftType('All Types');
        setAppliedStartDate('');
        setAppliedEndDate('');
        setAppliedType('All Types');
        setSearchQuery('');
        setCurrentPage(1);
    };

    const filteredLedger = useMemo(() => {
        let list = [...ledger];

        // Date Filter
        if (appliedStartDate) {
            list = list.filter(item => new Date(item.date) >= new Date(appliedStartDate));
        }
        if (appliedEndDate) {
            list = list.filter(item => new Date(item.date) <= new Date(appliedEndDate));
        }

        // Transaction Type Filter
        if (appliedType !== 'All Types') {
            list = list.filter(item => {
                if (appliedType === 'Credit') return item.credit > 0;
                if (appliedType === 'Debit') return item.debit > 0;
                return true;
            });
        }

        // Search Filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            list = list.filter(item => 
                (item.narration || '').toLowerCase().includes(q) ||
                (item.remark || '').toLowerCase().includes(q) ||
                (item.checkNumber || '').toLowerCase().includes(q) ||
                (item.paymentCollector || '').toLowerCase().includes(q)
            );
        }

        return list;
    }, [ledger, appliedStartDate, appliedEndDate, appliedType, searchQuery]);

    const totalPages = Math.ceil(filteredLedger.length / pageSize);
    const pagedRows = filteredLedger.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const showingFrom = filteredLedger.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const showingTo = Math.min(filteredLedger.length, currentPage * pageSize);

    const pages = useMemo(() => {
        const out = [];
        const maxButtons = 5;
        let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i += 1) out.push(i);
        return out;
    }, [currentPage, totalPages]);

    if (loading) return <PageSkeleton />;

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">{bank?.bankName} - All Payment Details</h1>
                <button 
                  className="list-page-btn" 
                  onClick={() => navigate(-1)}
                  style={{ background: '#64748b', color: '#fff', fontSize: '13px', height: '32px', padding: '0 15px' }}
                >
                  Back
                </button>
            </div>

            <DataTable
                columns={[
                    { key: 'name', label: 'NAME/TRADE NAME', render: item => item.narration.split(' - ')[1] || item.narration, style: { fontWeight: '500' } },
                    { key: 'type', label: 'TRANSACTION TYPE', render: item => item.credit > 0 ? 'Credit' : 'Debit' },
                    { key: 'amount', label: 'PAYMENT AMOUNT', render: item => (item.credit || item.debit || 0).toFixed(2), style: { fontWeight: '600' } },
                    { key: 'date', label: 'PAYMENT DATE', render: item => new Date(item.date).toLocaleDateString('en-GB') },
                    { key: 'check_number', label: 'CHECK NUMBER', render: item => item.checkNumber || '' },
                    { key: 'remark', label: 'REMARK', render: item => item.remark || '' },
                    { key: 'collector', label: 'PAYMENT COLLECTOR', render: item => item.paymentCollector || '' }
                ]}
                data={pagedRows}
                loading={loading}
                emptyMessage="No transactions found"
                page={currentPage}
                pageSize={pageSize}
                totalEntries={filteredLedger.length}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                searchBar={
                    <div style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                        <SearchBar
                            label="SEARCH"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search transactions..."
                            style={{ width: '280px' }}
                        />

                        <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-start' }}>
                            <div className="list-page-filter-group" style={{ flex: '0 0 155px' }}>
                                <label className="list-page-filter-label" style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px', display: 'block', textTransform: 'none' }}>Start Date</label>
                                <AirDatePicker
                                    value={draftStartDate}
                                    onChange={(val) => setDraftStartDate(val)}
                                    placeholder="Start Date"
                                    className="list-page-filter-date"
                                />
                            </div>

                            <div className="list-page-filter-group" style={{ flex: '0 0 155px' }}>
                                <label className="list-page-filter-label" style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px', display: 'block', textTransform: 'none' }}>End Date</label>
                                <AirDatePicker
                                    value={draftEndDate}
                                    onChange={(val) => setDraftEndDate(val)}
                                    placeholder="End Date"
                                    className="list-page-filter-date"
                                />
                            </div>

                            <div className="list-page-filter-group" style={{ flex: '0 0 195px' }}>
                                <label className="list-page-filter-label" style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px', display: 'block', textTransform: 'none' }}>Transaction Type</label>
                                <select 
                                  className="list-page-filter-select" 
                                  value={draftType} 
                                  onChange={(e) => setDraftType(e.target.value)}
                                  style={{ height: '38px', borderRadius: '8px', fontSize: '13px' }}
                                >
                                    <option value="All Types">---Select Type---</option>
                                    <option value="Credit">Credit</option>
                                    <option value="Debit">Debit</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', gap: '2px', marginTop: '25px' }}>
                                <button 
                                  className="list-page-btn list-page-btn-submit" 
                                  style={{ height: '38px', fontWeight: 'bold', minWidth: '85px', borderRadius: '6px', padding: '0 10px', fontSize: '14px' }}
                                  onClick={handleSubmitFilters}
                                >
                                  Submit
                                </button>
                                <button 
                                  className="list-page-btn list-page-btn-reset" 
                                  style={{ height: '38px', fontWeight: 'bold', minWidth: '85px', borderRadius: '6px', padding: '0 10px', fontSize: '14px' }}
                                  onClick={handleClearFilters}
                                >
                                  Reset
                                </button>
                            </div>
                        </div>
                    </div>
                }
            />
        </div>
    );
}

export default BankTransactions;

